import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { qk } from '@/lib/query-client'
import { APPROVER_ROLES, MANAGER_ROLES, WRITER_ROLES } from '@/lib/constants'
import { useAuth } from './auth-provider'
import type { Tables, UserRole } from '@/types/database'

export interface Membership {
  role: UserRole
  workspace: Tables<'workspaces'>
}

interface WorkspaceContextValue {
  memberships: Membership[]
  currentWorkspace: Tables<'workspaces'> | null
  workspaceId: string | null
  role: UserRole | null
  isLoading: boolean
  setCurrentWorkspace: (id: string) => void
  refetch: () => void
  can: {
    write: boolean
    manage: boolean
    approve: boolean
    viewOnly: boolean
  }
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)
const STORAGE_KEY = 'texa-hub-workspace'

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [currentId, setCurrentId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY))

  const { data, isLoading, refetch } = useQuery({
    queryKey: qk.workspaces,
    enabled: !!user,
    queryFn: async (): Promise<Membership[]> => {
      const { data, error } = await supabase
        .from('workspace_members')
        .select('role, workspace:workspaces(*)')
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data ?? [])
        .filter((m) => m.workspace)
        .map((m) => ({ role: m.role, workspace: m.workspace as unknown as Tables<'workspaces'> }))
    },
  })

  const memberships = useMemo(() => data ?? [], [data])

  // Garante um workspace atual válido.
  useEffect(() => {
    if (!memberships.length) return
    const stillValid = currentId && memberships.some((m) => m.workspace.id === currentId)
    if (!stillValid) {
      const first = memberships[0]!.workspace.id
      setCurrentId(first)
      localStorage.setItem(STORAGE_KEY, first)
    }
  }, [memberships, currentId])

  const current = useMemo(
    () => memberships.find((m) => m.workspace.id === currentId) ?? memberships[0] ?? null,
    [memberships, currentId],
  )

  const role = current?.role ?? null

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      memberships,
      currentWorkspace: current?.workspace ?? null,
      workspaceId: current?.workspace.id ?? null,
      role,
      isLoading,
      setCurrentWorkspace: (id) => {
        setCurrentId(id)
        localStorage.setItem(STORAGE_KEY, id)
      },
      refetch,
      can: {
        write: !!role && WRITER_ROLES.includes(role),
        manage: !!role && MANAGER_ROLES.includes(role),
        approve: !!role && APPROVER_ROLES.includes(role),
        viewOnly: role === 'visualizador',
      },
    }),
    [memberships, current, role, isLoading, refetch],
  )

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace deve estar dentro de <WorkspaceProvider>')
  return ctx
}
